import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Shield, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  isDefault: boolean;
  createdAt?: number;
  created_at?: number;
}

function Roles() {
  const { t } = useTranslation();
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<Role | null>(null);
  const [newRole, setNewRole] = useState({
    name: '',
    description: '',
    permissions: '',
  });

  useEffect(() => {
    fetchRoles();
  }, []);

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/roles`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRoles(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch roles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateRole = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      // Convert comma-separated permissions string to array
      const permissionsArray = newRole.permissions
        .split(',')
        .map(p => p.trim())
        .filter(p => p.length > 0);

      const response = await fetch(`${apiUrl}/api/roles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: newRole.name,
          description: newRole.description,
          permissions: permissionsArray,
        }),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewRole({ name: '', description: '', permissions: '' });
        fetchRoles();
      }
    } catch (error) {
      console.error('Failed to create role:', error);
    }
  };

  const handleEditRole = async () => {
    if (!selectedRole) return;

    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/roles/${selectedRole.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(selectedRole),
      });

      if (response.ok) {
        setIsEditDialogOpen(false);
        setSelectedRole(null);
        fetchRoles();
      }
    } catch (error) {
      console.error('Failed to update role:', error);
    }
  };

  const openEditDialog = (role: Role) => {
    setSelectedRole({ ...role });
    setIsEditDialogOpen(true);
  };

  if (isLoading) {
    return (
      <div className="w-full px-4 py-6 sm:px-6 lg:px-8">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">{t('common.loading')}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="w-full space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      <PageHeader
        eyebrow="Users & access"
        icon={Shield}
        title={t('roles.title')}
        subtitle={t('roles.subtitle')}
        actions={(
          <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('roles.createRole')}
          </Button>
        )}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('roles.allRoles')}</CardTitle>
          <CardDescription>
            {t('roles.description', { count: roles.length })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('roles.table.name')}</TableHead>
                <TableHead>{t('roles.table.description')}</TableHead>
                <TableHead>{t('roles.table.permissions')}</TableHead>
                <TableHead>{t('roles.table.type')}</TableHead>
                <TableHead>{t('roles.table.created')}</TableHead>
                <TableHead className="text-right">{t('roles.table.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {roles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    {t('dashboard.noData')}
                  </TableCell>
                </TableRow>
              ) : (
                roles.map((role) => (
                  <TableRow key={role.id}>
                    <TableCell className="font-medium">{role.name}</TableCell>
                    <TableCell className="max-w-md break-words">{role.description}</TableCell>
                    <TableCell>
                      <Badge variant="outline">{role.permissions.length} {t('roles.table.permissions')}</Badge>
                    </TableCell>
                    <TableCell>
                      {role.isDefault ? (
                        <Badge variant="outline" className="border-primary/20 bg-primary/10 text-primary">
                          {t('common.default')}
                        </Badge>
                      ) : (
                        <Badge variant="outline">{t('common.custom')}</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {formatDate(role.created_at || role.createdAt)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(role)}>
                        {t('common.edit')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Role Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('roles.dialogs.create.title')}</DialogTitle>
            <DialogDescription>
              {t('roles.dialogs.create.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t('roles.table.name')}</Label>
              <Input
                id="name"
                value={newRole.name}
                onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                placeholder={t('roles.createRole')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="description">{t('roles.table.description')}</Label>
              <Input
                id="description"
                value={newRole.description}
                onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                placeholder={t('roles.table.description')}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="permissions">{t('roles.dialogs.create.permissionsLabel')}</Label>
              <Textarea
                id="permissions"
                value={newRole.permissions}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNewRole({ ...newRole, permissions: e.target.value })}
                placeholder={t('roles.dialogs.create.permissionsPlaceholder')}
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleCreateRole} disabled={!newRole.name || !newRole.description}>
              {t('roles.createRole')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Role Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('roles.dialogs.edit.title')}</DialogTitle>
            <DialogDescription>
              {t('roles.dialogs.edit.description')}
            </DialogDescription>
          </DialogHeader>
          {selectedRole && (
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="edit-name">{t('roles.table.name')}</Label>
                <Input
                  id="edit-name"
                  value={selectedRole.name}
                  onChange={(e) => setSelectedRole({ ...selectedRole, name: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-description">{t('roles.table.description')}</Label>
                <Input
                  id="edit-description"
                  value={selectedRole.description}
                  onChange={(e) => setSelectedRole({ ...selectedRole, description: e.target.value })}
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="edit-permissions">{t('roles.dialogs.create.permissionsLabel')}</Label>
                <Textarea
                  id="edit-permissions"
                  value={selectedRole.permissions.join(', ')}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    const permissionsArray = e.target.value
                      .split(',')
                      .map((p: string) => p.trim())
                      .filter((p: string) => p.length > 0);
                    setSelectedRole({ ...selectedRole, permissions: permissionsArray });
                  }}
                  rows={4}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button onClick={handleEditRole}>
              {t('common.update')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default Roles;
